import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Clock } from "lucide-react";
import AppLayout from "../components/AppLayout";
import api, { formatApiError } from "../lib/api";

import { _t } from "@/lib/i18n";
/**
 * Payment Success page.
 * NEVER trusts the redirect as proof of payment. Instead, polls the backend
 * `/api/payments/status/{session_id}` which itself re-checks Stripe on demand.
 * Only when the server confirms `payment_status === "paid"` do we tell the user
 * that the service is active.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);       // pending | paid | failed | expired | error
  const [service, setService] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError(_t("session_id mancante nella URL"));
      return;
    }
    let cancelled = false;
    const maxAttempts = 25; // ~50s @ 2s
    const poll = async () => {
      let n = 0;
      while (!cancelled && n < maxAttempts) {
        n++;
        setAttempts(n);
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          setService(data.service);
          if (data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          if (["failed", "expired"].includes(data.payment_status)) {
            setStatus(data.payment_status);
            return;
          }
        } catch (e) {
          setError(formatApiError(e));
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const nextPath =
    service === "racconti_entry" ? "/racconti/dashboard" :
    service === "miab_launch" ? "/miab" :
    "/dashboard";

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto py-12">
        <div className="tc-card p-8 md:p-12 text-center">
          {status === null || status === "pending" || (status === null && !error) ? (
            <PendingState attempts={attempts} />
          ) : status === "paid" ? (
            <PaidState onGoNext={() => nav(nextPath)} service={service} />
          ) : status === "timeout" ? (
            <TimeoutState sessionId={sessionId} />
          ) : status === "failed" || status === "expired" ? (
            <FailedState reason={status} onRetry={() => nav(nextPath)} />
          ) : (
            <ErrorState message={error} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function PendingState({ attempts }) {
  return (
    <div data-testid="payment-success-pending">
      <div className="inline-flex w-16 h-16 items-center justify-center rounded-full border border-amber-400/30 bg-amber-400/10 mb-5">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400 mb-2">{_t("Verifica in corso")}</div>
      <h2 className="font-display text-3xl text-white mb-3">{_t("Stiamo confermando il tuo pagamento")}</h2>
      <p className="text-white/60">{_t("Non è ancora finita. Stiamo controllando la conferma da Stripe. Non chiudere questa pagina.")}</p>
      <p className="text-xs text-white/40 mt-4">{_t("Tentativo {0}/25", attempts)}</p>
    </div>
  );
}

function PaidState({ onGoNext, service }) {
  const label = service === "racconti_entry"
    ? _t("Iscrizione autore attiva")
    : service === "miab_launch"
    ? _t("MIAB sigillato")
    : _t("Servizio attivato");
  return (
    <div data-testid="payment-success-paid">
      <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 mb-5 shadow-lg shadow-emerald-500/30">
        <CheckCircle2 className="w-8 h-8 text-white" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-2 flex items-center justify-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" />{" "}{_t("Pagamento confermato")}</div>
      <h2 className="font-display text-3xl text-white mb-3">{label}</h2>
      <p className="text-white/60 mb-8">{_t("Abbiamo verificato la conferma direttamente con Stripe. Tutto pronto per proseguire.")}</p>
      <button data-testid="payment-success-continue" onClick={onGoNext} className="btn-launch">{_t("Prosegui")}</button>
    </div>
  );
}

function TimeoutState({ sessionId }) {
  return (
    <div data-testid="payment-success-timeout">
      <div className="inline-flex w-16 h-16 items-center justify-center rounded-full border border-amber-400/40 bg-amber-500/10 mb-5">
        <Clock className="w-8 h-8 text-amber-400" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400 mb-2">{_t("Attesa più lunga del previsto")}</div>
      <h2 className="font-display text-3xl text-white mb-3">{_t("Verifica in corso lato Stripe")}</h2>
      <p className="text-white/60 mb-4">{_t("Il tuo pagamento potrebbe essere ancora in elaborazione da parte del circuito. Ti aggiorneremo appena confermato. Se hai completato il pagamento, puoi ricaricare la pagina o riprovare tra qualche minuto.")}</p>
      <div className="text-xs text-white/40 font-mono-launch">{_t("session {0}", sessionId)}</div>
    </div>
  );
}

function FailedState({ reason, onRetry }) {
  return (
    <div data-testid="payment-success-failed">
      <div className="inline-flex w-16 h-16 items-center justify-center rounded-full border border-red-400/40 bg-red-500/10 mb-5">
        <XCircle className="w-8 h-8 text-red-400" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-red-400 mb-2">{_t("Pagamento non completato")}</div>
      <h2 className="font-display text-3xl text-white mb-3">
        {reason === "expired" ? _t("Sessione scaduta") : _t("Pagamento fallito")}
      </h2>
      <p className="text-white/60 mb-8">{_t("Non abbiamo ricevuto la conferma da Stripe. Il servizio non è stato attivato.")}</p>
      <button onClick={onRetry} className="btn-ghost">{_t("Torna indietro")}</button>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div data-testid="payment-success-error">
      <div className="inline-flex w-16 h-16 items-center justify-center rounded-full border border-red-400/40 bg-red-500/10 mb-5">
        <XCircle className="w-8 h-8 text-red-400" />
      </div>
      <h2 className="font-display text-3xl text-white mb-3">{_t("Errore")}</h2>
      <p className="text-white/60">{message}</p>
    </div>
  );
}
